import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw, FileText } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { BottomSheet } from '../../../../components/ui/BottomSheet';
import { LABEL_STANDARD } from '../../../../lib/formStyles';
import {
  CABLE_TYPE_OPTIONS,
  CABLE_CSA_OPTIONS,
  getResistancePerMeter,
} from './voltDropDCCalcData';

const INPUT_BASE =
  'flex h-9 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

function SelectTrigger({ label, placeholder, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${INPUT_BASE} text-left ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <span className={label ? '' : 'text-gray-500 dark:text-gray-400'}>
        {label || placeholder}
      </span>
      <svg
        className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  );
}

function RadioList({ options, value, onChange, onSelect }) {
  return (
    <div className="space-y-1">
      {options.map((opt) => {
        const optVal = opt.value !== undefined && opt.value !== null ? opt.value : opt.id;
        const isChecked = value === optVal || (opt.id === '' && !value);
        return (
          <label
            key={opt.id || opt.label}
            className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
          >
            <input
              type="radio"
              name={String(opt.id)}
              value={optVal}
              checked={isChecked}
              onChange={() => {
                onChange(opt.id === '' ? '' : optVal);
                onSelect?.();
              }}
              className="h-4 w-4 text-blue-600"
            />
            <span className="text-gray-900 dark:text-gray-100">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

const initialState = {
  load: '',
  dcVoltage: '',
  circuitLength: '',
  cableCsa: '',
  cableType: '',
};

export default function VoltDropDCCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [form, setForm] = useState(initialState);
  const [result, setResult] = useState(null);
  const [openSheet, setOpenSheet] = useState(null);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateForm = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const getLabel = (options, value, fallback = 'Select') => {
    if (!value) return fallback;
    const opt = options.find((o) => (o.value ?? o.id) === value);
    return opt?.label ?? fallback;
  };

  const handleCalculate = () => {
    const Ib = parseFloat(form.load);
    const U = parseFloat(form.dcVoltage);
    const L = parseFloat(form.circuitLength);
    const r = getResistancePerMeter(form.cableCsa);

    if (!Ib || !U || !L || !r) return;

    // DC radial: V_drop = 2 × I × L × r (go and return)
    const vdVolts = 2 * Ib * L * r;
    const vdPercent = (vdVolts / U) * 100;
    const fullLength = L * 2;

    setResult({
      voltDropVolts: vdVolts.toFixed(4),
      voltDropPercent: vdPercent.toFixed(2),
      fullCircuitLength: fullLength.toFixed(2),
      resistancePerMeter: r.toFixed(6),
    });
  };

  const handleReset = () => {
    setForm(initialState);
    setResult(null);
  };

  const canCalculate =
    form.load &&
    form.dcVoltage &&
    form.circuitLength &&
    form.cableCsa &&
    form.cableType;

  const selectedCableType = CABLE_TYPE_OPTIONS.find((o) => o.value === form.cableType);
  const dataSheetUrl = selectedCableType?.dataSheetUrl;

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">DC Volt Drop Calculator</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      {/* Red info banner */}
      <div className="mt-4 px-4 py-3 rounded-lg bg-red-600 dark:bg-red-700 text-white text-sm">
        Voltage drop calculator for DC radial circuits
      </div>

      <div className="space-y-4 pb-6 mt-4">
        {/* Load (Ib) and DC Voltage (U) - side by side on larger screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_STANDARD}>Load (Ib)</label>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
                A
              </span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                placeholder="Amps"
                value={form.load}
                onChange={(e) => updateForm('load', e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
              />
            </div>
          </div>
          <div>
            <label className={LABEL_STANDARD}>DC Voltage (U)</label>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
                V
              </span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                placeholder="Volts"
                value={form.dcVoltage}
                onChange={(e) => updateForm('dcVoltage', e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Circuit Length (1 way) */}
        <div>
          <label className={LABEL_STANDARD}>Circuit Length (1 way)</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">
              L
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              placeholder="Meters"
              value={form.circuitLength}
              onChange={(e) => updateForm('circuitLength', e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Cable CSA and Cable Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_STANDARD}>Cable CSA (mm²)</label>
            <SelectTrigger
              label={form.cableCsa ? `${form.cableCsa}` : null}
              placeholder="Select"
              onClick={() => setOpenSheet('cableCsa')}
            />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Cable Type</label>
            <SelectTrigger
              label={getLabel(CABLE_TYPE_OPTIONS, form.cableType)}
              placeholder="Select"
              onClick={() => setOpenSheet('cableType')}
            />
          </div>
        </div>

        {/* Results table */}
        <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 bg-green-600 dark:bg-green-700">
            <div className="px-4 py-3 text-sm font-semibold text-white">Parameter</div>
            <div className="px-4 py-3 text-sm font-semibold text-white text-right flex items-center justify-end gap-1">
              Result <span className="text-xs font-normal opacity-90">V</span>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800">
            <div className="grid grid-cols-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">Volt Drop Volts</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.voltDropVolts} V` : '-'}
              </span>
            </div>
            <div className="grid grid-cols-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">Volt Drop Percent</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.voltDropPercent} %` : '-'}
              </span>
            </div>
            <div className="grid grid-cols-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">Full Circuit Length (Lx2)</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.fullCircuitLength} Meters` : '-'}
              </span>
            </div>
            <div className="grid grid-cols-2 px-4 py-3">
              <span className="text-sm text-gray-700 dark:text-gray-300">Resistance Per Meter</span>
              <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                {result ? `${result.resistancePerMeter} Ω/Meter` : '-'}
              </span>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-600 dark:text-gray-400">
          Based on a conductor temperature of 20°C
        </p>

        {/* View Cable Data Sheet */}
        {dataSheetUrl && (
          <Button
            variant="outline"
            onClick={() => window.open(dataSheetUrl, '_blank', 'noopener,noreferrer')}
            className="w-full border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <FileText className="h-4 w-4 mr-2" />
            View Cable Data Sheet
          </Button>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Selection BottomSheets */}
      <BottomSheet
        open={openSheet === 'cableCsa'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        title="Select"
        maxHeight="max-h-[85vh]"
      >
        <RadioList
          options={[{ id: '', label: 'Select', value: '' }, ...CABLE_CSA_OPTIONS]}
          value={form.cableCsa}
          onChange={(v) => updateForm('cableCsa', v)}
          onSelect={() => setOpenSheet(null)}
        />
      </BottomSheet>
      <BottomSheet
        open={openSheet === 'cableType'}
        onOpenChange={(o) => !o && setOpenSheet(null)}
        title="Select"
        maxHeight="max-h-[85vh]"
      >
        <RadioList
          options={CABLE_TYPE_OPTIONS}
          value={form.cableType}
          onChange={(v) => updateForm('cableType', v)}
          onSelect={() => setOpenSheet(null)}
        />
      </BottomSheet>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            DC Volt Drop Calculator
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate DC voltage drop for radial circuits (e.g. solar PV)
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
